var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useAlertUiTranslation } from '../../../../../../ui/hooks';
import { showAlertDialog, showErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { requireAnyOrgPermission, requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { getTotalSize, handleSelectedFilesValidation, useGetSelectedFilesContent, } from '@juvare/document-library/ui/features/file-selection';
import React from 'react';
import { maxFilesCount, maxFilesSize, useShowSelectFilesToAttachDialog } from '../../../hooks/useShowSelectFilesToAttachDialog';
import { ValidationErrorModalContent } from '../Components/ValidationErrorModalContent';
export const useEditorConfig = ({ additionalRichTextEditorMenuItems, handleSaveAttachments, preselectedFilesInfo, responsePlaceholder, }) => {
    var _a;
    const { t } = useAlertUiTranslation();
    const inputFileRef = React.useRef(null);
    const preselectedFilesInfoRef = React.useRef(preselectedFilesInfo);
    preselectedFilesInfoRef.current = preselectedFilesInfo;
    const handleValidateAndSaveAttachments = (selectedFiles, preselectedFilesInfo, handleClose) => __awaiter(void 0, void 0, void 0, function* () {
        if (selectedFiles.length === 0) {
            return;
        }
        const selectedFilesCount = selectedFiles.length + preselectedFilesInfo.count;
        const selectedFilesSize = getTotalSize(selectedFiles) + preselectedFilesInfo.size;
        const { isMaxFilesCountExceeded, isMaxFilesSizeExceeded } = handleSelectedFilesValidation({
            selectedFilesCount,
            selectedFilesSize,
            maxFilesSize,
            maxFilesCount,
        });
        if (isMaxFilesCountExceeded || isMaxFilesSizeExceeded) {
            showAlertDialog({
                title: t('pages.edit.richTextEditors.selectFilesWizard.validationErrorModal.title'),
                content: (React.createElement(ValidationErrorModalContent, { selectedFilesCount: selectedFilesCount, selectedFilesSize: selectedFilesSize, maxFilesCount: maxFilesCount, maxFilesSize: maxFilesSize })),
            });
        }
        else {
            yield handleSaveAttachments(selectedFiles);
            handleClose === null || handleClose === void 0 ? void 0 : handleClose();
        }
    });
    const getSelectedFilesContent = useGetSelectedFilesContent();
    const handleFilesAttach = (_a) => __awaiter(void 0, [_a], void 0, function* ({ selectedFiles, onClose }) {
        try {
            const files = yield getSelectedFilesContent(selectedFiles);
            yield handleValidateAndSaveAttachments(files, preselectedFilesInfoRef.current, onClose);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        }
        catch (error) {
            showErrorDialog({ title: t('pages.edit.richTextEditors.selectFilesWizard.errorWhenAttachingFiles') });
        }
    });
    const menuItems = [
        {
            type: 'menuitem',
            text: t('pages.edit.richTextEditors.uploadFile'),
            onAction: () => inputFileRef.current && inputFileRef.current.click(),
        },
    ];
    const userState = useUserState();
    const isUserAllowedToManageDocuments = requireAnySystemPermission(userState, 'manage-documents') ||
        requireAnyOrgPermission(userState, 'manage-documents', 'view-documents');
    const showSelectFilesToAttachWizard = useShowSelectFilesToAttachDialog();
    if (isUserAllowedToManageDocuments) {
        menuItems.push({
            type: 'menuitem',
            text: t('pages.edit.richTextEditors.selectInFileLibrary'),
            onAction: () => showSelectFilesToAttachWizard({
                preselectedFilesInfoRef,
                onFilesSelect: handleFilesAttach,
            }),
        });
        const customAttachmentMenuItems = (_a = additionalRichTextEditorMenuItems === null || additionalRichTextEditorMenuItems === void 0 ? void 0 : additionalRichTextEditorMenuItems.filter((item) => item.key === 'addAttachmentButton')) !== null && _a !== void 0 ? _a : [];
        menuItems.push(...customAttachmentMenuItems.map((item) => ({
            type: 'menuitem',
            text: item.label,
            onAction: () => item.onClick({ preselectedFilesInfoRef, onFilesSelect: handleFilesAttach }),
        })));
    }
    const orphanMenuItems = (additionalRichTextEditorMenuItems !== null && additionalRichTextEditorMenuItems !== void 0 ? additionalRichTextEditorMenuItems : []).filter((item) => item.key !== 'addAttachmentButton');
    const toolbar = [
        `undo redo | bold italic underline strikethrough | addAttachmentButton | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor removeformat | link | image table | addPlaceholderButton`,
        ...orphanMenuItems.map((item) => item.key),
    ].join(' | ');
    const editorInit = {
        // Toolbar is overriden only to make addAttachmentButton visible without expanding it.
        // If making changes (e.g., adding new buttons), do not forget to update toolbar accordingly.
        toolbar,
        setup: (editor) => {
            editor.ui.registry.addButton('addPlaceholderButton', {
                text: t('pages.edit.richTextEditors.responsePlaceholder'),
                onAction: () => editor.insertContent(responsePlaceholder),
                tooltip: t('pages.edit.richTextEditors.responsePlaceholderTooltip'),
            });
            editor.ui.registry.addIcon('attachment', '<svg height="24" width="24"><path d="M2 12.5C2 9.46 4.46 7 7.5 7H18c2.21 0 4 1.79 4 4s-1.79 4-4 4H9.5C8.12 15 7 13.88 7 12.5S8.12 10 9.5 10H17v2H9.41c-.55 0-.55 1 0 1H18c1.1 0 2-.9 2-2s-.9-2-2-2H7.5C5.57 9 4 10.57 4 12.5S5.57 16 7.5 16H17v2H7.5C4.46 18 2 15.54 2 12.5z"/></svg>');
            editor.ui.registry.addMenuButton('addAttachmentButton', {
                icon: 'attachment',
                fetch: (callback) => callback(menuItems),
            });
            orphanMenuItems.forEach((item) => {
                editor.ui.registry.addButton(item.key, {
                    text: item.label,
                    onAction: () => item.onClick({ preselectedFilesInfoRef, onFilesSelect: handleFilesAttach }),
                });
            });
        },
    };
    return { editorInit, inputFileRef, handleValidateAndSaveAttachments };
};
